// @ts-strict-ignore
import moment from 'moment';
import * as React from 'react';
import type { QueryFunctionContext } from 'react-query';
import { useQuery, useQueryClient } from 'react-query';

import { BOARDS_API_ROUTES } from '@/api-routes';
import { axiosInstance } from '@/axios';
import { timeInMs } from '@/constants/time';
import type { CustomFeedNavDataType } from '@/types';

export type FeedsResponse = {
  results: Array<CustomFeedNavDataType>;
};

const fetcher = async ({ signal, queryKey }: QueryFunctionContext) => {
  const hidden = Array.isArray(queryKey) ? queryKey[2] : undefined;
  return await axiosInstance.get<FeedsResponse>(BOARDS_API_ROUTES.index(), {
    signal,
    ...(hidden === true ? { params: { hidden } } : {}),
  });
};

export const NAVIGATION_FEEDS = ['navigation', 'feeds'];

export const useFetchFeeds = (hidden: boolean = false) => {
  return useQuery([...NAVIGATION_FEEDS, hidden], fetcher, {
    staleTime: timeInMs.FIFTEEN_SECONDS,
    select: (data) => {
      const results = data.data.results;
      const currentEpoch = moment().unix();
      const currentDate = moment.unix(currentEpoch);

      return results.map((result) => {
        const resultDate = moment.unix(result.created_at);
        return {
          ...result,
          isNew: resultDate.isSame(currentDate, 'day'),
        };
      });
    },
  });
};

export const useInvalidateFetchFeeds = () => {
  const queryClient = useQueryClient();

  return React.useCallback(() => {
    return queryClient.invalidateQueries(NAVIGATION_FEEDS);
  }, [queryClient]);
};

export const usePrefetchFeeds = () => {
  const queryClient = useQueryClient();

  React.useEffect(() => {
    queryClient.prefetchQuery(NAVIGATION_FEEDS, {
      queryFn: fetcher,
      staleTime: timeInMs.FIFTEEN_SECONDS,
    });
  }, [queryClient]);
};

export const useGetFilteredFeeds = () => {
  const queryData = useFetchFeeds();

  const filteredFeedData = React.useMemo(() => {
    return queryData.data?.filter((data) => data.board_name !== 'home');
  }, [queryData.data]);

  return {
    ...queryData,
    data: filteredFeedData,
  };
};
